import { useEffect, useState } from 'react'
import type {
  AgendaItem, BoardMember, Commitment, HomeMetrics, NextMeeting,
} from '../../api/client'
import { endpoints } from '../../api/client'
import {
  AskBox, CompanyCard, NextMeetingCard, Roster, TableCard, YourWordCard,
} from './Widgets'
import { SeasonalCard, ThisWeek } from './Activity'
import { Masthead } from './Masthead'
import { ProfileNudge } from '../../components/ProfileNudge'

/*
  Home as a bento grid (UI SPEC §4).

  It used to render three elements and eight hundred pixels of nothing, which told an owner
  the product had nothing to say. Every sidebar destination now has a live widget here: a
  preview, a jump, or something you can act on without leaving.

  The grid is deliberately not equal-weight. The next meeting is the largest thing on the
  page and the only one filled in the brand colour, because it is the thing the whole
  product is arranged around. Everything else is a way back to it.
*/

interface Props {
  company: string | null
  next: NextMeeting | null
  inbox: AgendaItem[]
  commitments: Commitment[]
  board: BoardMember[]
  onNavigate: (screen: 'table' | 'commitments' | 'meetings' | 'board' | 'company' | 'numbers') => void
  onAsked: (consultId: string) => void
  onCaptured: (text: string) => Promise<void>
  onResolve: (id: string, status: string, note?: string) => Promise<void>
  onCapture: () => void
  onOpenSeat: (memberId: string) => void
  /** Open one item on The Table, rather than the screen. */
  onOpenItem: (id: string) => void
}

export function Home({
  company, next, inbox, commitments, board,
  onNavigate, onAsked, onCaptured, onResolve, onCapture, onOpenSeat, onOpenItem,
}: Props) {
  // Lifted here rather than held in the ask box, because it is set from the roster above
  // it. Hovering Manny and typing into a box still addressed to everyone would be a lie.
  const [scopedTo, setScopedTo] = useState<BoardMember | null>(null)

  /*
    One read, two readers. The strip under the company name shows the figures and the card
    below shows the flag, and they must not disagree or fetch twice. Lifted here rather
    than left in the card because the masthead is not a widget and cannot own a fetch of
    its own without the two going out of step.

    A failure leaves it null, which draws neither: this endpoint spent three days returning
    500 to every business with a fiscal year end, and the whole of Home stayed usable
    because nothing here treats it as required.
  */
  const [metrics, setMetrics] = useState<HomeMetrics | null>(null)
  useEffect(() => {
    void endpoints.homeMetrics().then(setMetrics).catch(() => setMetrics(null))
  }, [])

  /*
    Two columns, not three.

    The old grid placed cards on a twelve-column field row by row, so widths changed between
    rows for no stated reason, card bottoms in a row never aligned, and the last row left a
    hole where its second card would have been. Cards in a rail align because they share an
    edge, which is the whole of the fix.

    The wide column carries the things you read and write into; the rail carries state. On a
    phone both wrappers become display: contents, so the eight cards flatten back into one
    ordered column and the roster still goes last.
  */
  return (
    <>
      <Masthead
        company={company} next={next} inbox={inbox} commitments={commitments}
        metrics={metrics} onNavigate={onNavigate}
      />

      <ProfileNudge onNavigate={onNavigate} />

      <div className="bento">
        <div className="bento__main">
          <AskBox
            scopedTo={scopedTo}
            board={board}
            onScopeAsk={setScopedTo}
            onAsked={onAsked}
            onCaptured={onCaptured}
          />

          <TableCard
            items={inbox}
            onNavigate={() => onNavigate('table')}
            onCaptured={onCaptured}
            onOpenItem={onOpenItem}
          />

          {/* Collapses when empty rather than sitting there as a hollow box. */}
          <ThisWeek board={board} />

          {/*
            The standing question reads here rather than in the rail. It is something the
            board is putting to you and you can act on, which is the same species as the ask
            box above it; the rail is for state. It also happens to be what balances the two
            columns: with it on the right the rail ran about five hundred pixels past the
            bottom of the left column and left a hole there.
          */}
          <SeasonalCard board={board} onCaptured={onCaptured} />
        </div>

        <aside className="bento__rail">
          <NextMeetingCard
            next={next}
            board={board}
            onNavigate={onNavigate}
            onCapture={onCapture}
          />

          <YourWordCard
            commitments={commitments}
            onNavigate={() => onNavigate('commitments')}
            onResolve={onResolve}
          />

          <CompanyCard metrics={metrics} onCaptured={onCaptured} />

          <Roster
            members={board}
            onOpenSeat={onOpenSeat}
            onScopeAsk={setScopedTo}
            scopedTo={scopedTo}
          />
        </aside>
      </div>
    </>
  )
}
